import { z } from "zod";

import { convertEnumToSchema } from "./atomic-operations/aoAbstractionsInDev/utils.js";
import { CellType, HexTypeLiteral } from "./enums.js";
import {
  CellId,
  DataConnectionId,
  DataSourceTableId,
  FileId,
  GroupId,
  HexId,
  SemanticDatasetId,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticViewId,
  SemanticViewName,
  TaoProjectId,
  UserId,
} from "./idTypeBrands.js";
import { MentionedDataframeName } from "./MagicTypes.js";

export const RichTextMarkBold = z.literal("bold");
export const RichTextMarkItalic = z.literal("italic");
export const RichTextMarkUnderline = z.literal("underline");
export const RichTextMarkStrikethrough = z.literal("strikethrough");
export const RichTextMarkCode = z.literal("code");
export const RichTextMarkSelection = z.literal("selection");
export type RichTextMarkBold = z.infer<typeof RichTextMarkBold>;
export type RichTextMarkItalic = z.infer<typeof RichTextMarkItalic>;
export type RichTextMarkUnderline = z.infer<typeof RichTextMarkUnderline>;
export type RichTextMarkStrikethrough = z.infer<
  typeof RichTextMarkStrikethrough
>;
export type RichTextMarkCode = z.infer<typeof RichTextMarkCode>;
export type RichTextMarkSelection = z.infer<typeof RichTextMarkSelection>;

export const RichTextMark = z.union([
  RichTextMarkBold,
  RichTextMarkItalic,
  RichTextMarkUnderline,
  RichTextMarkStrikethrough,
  RichTextMarkCode,
]);
export type RichTextMark = z.infer<typeof RichTextMark>;

export const RichTextP = z.literal("p");
/**
 * @deprecated use RichTextP ("p") type instead.
 */
export const RichTextPLegacy = z.literal("paragraph");
export const RichTextH1 = z.literal("h1");
export const RichTextH2 = z.literal("h2");
export const RichTextH3 = z.literal("h3");
export const RichTextH4 = z.literal("h4");
export const RichTextH5 = z.literal("h5");
/**
 * @deprecated use RichTextH5 ("h5") type instead.
 */
export const RichTextH6 = z.literal("h6");
export const RichTextBlockquote = z.literal("blockquote");

export type RichTextHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export const RichTextParagraph = z.union([RichTextP, RichTextPLegacy]);
export type RichTextParagraph = z.infer<typeof RichTextParagraph>;

export const RichTextCodeblock = z.literal("codeblock");
export type RichTextCodeblock = z.infer<typeof RichTextCodeblock>;

export const RichTextHeading = z.union([
  RichTextH1,
  RichTextH2,
  RichTextH3,
  RichTextH4,
  RichTextH5,
  RichTextH6,
]);
export type RichTextHeading = z.infer<typeof RichTextHeading>;

export const RichTextOL = z.literal("ol");
export const RichTextUL = z.literal("ul");
export const RichTextLI = z.literal("li");
export const RichTextLIC = z.literal("lic"); // list item content
export type RichTextOL = z.infer<typeof RichTextOL>;
export type RichTextUL = z.infer<typeof RichTextUL>;
export type RichTextLI = z.infer<typeof RichTextLI>;
export type RichTextLIC = z.infer<typeof RichTextLIC>;

export const RichTextLink = z.literal("a");
export type RichTextLink = z.infer<typeof RichTextLink>;

export const RichTextReference = z.literal("hex_reference");
export type RichTextReference = z.infer<typeof RichTextReference>;

export const RichTextImage = z.literal("image");
export type RichTextImage = z.infer<typeof RichTextImage>;

export const RichTextTextType = z.union([
  RichTextParagraph,
  RichTextHeading,
  RichTextBlockquote,
  RichTextCodeblock,
]);
export type RichTextTextType = z.infer<typeof RichTextTextType>;

export const RichTextListType = z.union([RichTextOL, RichTextUL]);
export type RichTextListType = z.infer<typeof RichTextListType>;

export const RichTextElementType = z.union([
  RichTextTextType,
  RichTextListType,
  RichTextLI,
]);
export type RichTextElementType = z.infer<typeof RichTextElementType>;

export const RichTextMentionElementType = z.literal("mention");
export type RichTextMentionElementType = z.infer<
  typeof RichTextMentionElementType
>;

export const RichTextMentionInputElementType = z.literal("mention_input");
export type RichTextMentionInputElementType = z.infer<
  typeof RichTextMentionInputElementType
>;

/**
 * Transient inline element the Plate emoji plugin inserts while the user is
 * typing a `:shortcode:`. Continuous multiplayer saves can persist it, so it
 * must be a valid document node.
 */
export const RichTextEmojiInputElementType = z.literal("emoji_input");
export type RichTextEmojiInputElementType = z.infer<
  typeof RichTextEmojiInputElementType
>;

export const MentionTriggerType = z.literal("@");
export type MentionTriggerType = z.infer<typeof MentionTriggerType>;

export const FormattedText = z.object({
  text: z.string(),
  [RichTextMarkBold.value]: z.boolean().optional(),
  [RichTextMarkItalic.value]: z.boolean().optional(),
  [RichTextMarkUnderline.value]: z.boolean().optional(),
  [RichTextMarkStrikethrough.value]: z.boolean().optional(),
  [RichTextMarkCode.value]: z.boolean().optional(),
  [RichTextMarkSelection.value]: z.boolean().optional(),
  /**
   * Legacy editor used strikeThrough, but Plate uses strikethrough.
   * We can make plate use strikeThrough but given that the industry standard
   * for this is actually strikethrough, switch to that while maintaining backcompat
   * @deprecated use strikethrough instead
   */
  strikeThrough: z.boolean().optional(), // legacy
});
export type FormattedText = z.infer<typeof FormattedText>;

export const UserMention = z.object({
  type: RichTextMentionElementType,
  // this is used for serialization and while initially
  // loading. Should prefer loading the actual value by id.
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("user"),
  userId: UserId,
  id: z.string(),
  // This is needed during deserialization. Without this, the mention
  // will fail to deserialize as it will have an extra field.
  value: z.string().optional(),
});

export const GroupMention = z.object({
  type: RichTextMentionElementType,
  // this is used for serialization and while initially
  // loading. Should prefer loading the actual value by id.
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("group"),
  groupId: GroupId,
  id: z.string(),
});

export const TableMention = z.object({
  type: RichTextMentionElementType,
  // this is used for serialization and while initially
  // loading. Should prefer loading the actual value by id.
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("table"),
  table: z.string(),
  schema: z.string(),
  database: z.string().or(z.null()),
  id: z.string(),
  tableId: DataSourceTableId,
  connectionId: DataConnectionId,
});

export const DataframeMention = z.object({
  type: RichTextMentionElementType,
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("dataframe"),
  name: MentionedDataframeName,
  id: z.string(),
  connectionId: DataConnectionId.or(z.null()),
});

export const HexMention = z.object({
  type: RichTextMentionElementType,
  // this is used for serialization and while initially
  // loading. Should prefer loading the actual value by id.
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("hex"),
  id: z.string(),
  hexId: HexId,
  hexType: HexTypeLiteral,
});

export const TaoProjectMention = z.object({
  type: RichTextMentionElementType,
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("tao_project"),
  id: z.string(),
  taoProjectId: TaoProjectId,
});

export const HexMentionInput = z.object({
  type: RichTextMentionInputElementType,
  children: z.array(FormattedText),
});

export const EmojiInputElement = z.object({
  type: RichTextEmojiInputElementType,
  children: z.array(FormattedText),
});

export const CellMention = z.object({
  type: RichTextMentionElementType,
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("cell"),
  id: z.string(),
  cellId: CellId,
  name: z.string(),
  cellType: convertEnumToSchema(CellType),
});

export const SemanticDatasetMention = z.object({
  type: RichTextMentionElementType,
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("semantic_dataset"),
  id: z.string(),
  semanticDatasetId: SemanticDatasetId,
  name: SemanticDatasetName,
  semanticProjectId: SemanticProjectId,
});

export const SemanticViewMention = z.object({
  type: RichTextMentionElementType,
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("semantic_view"),
  id: z.string(),
  semanticViewId: SemanticViewId,
  name: SemanticViewName,
  semanticProjectId: SemanticProjectId,
});

export const FileMention = z.object({
  type: RichTextMentionElementType,
  previewText: z.string(),
  children: z.array(FormattedText),
  mentionType: z.literal("file"),
  id: z.string(),
  fileId: FileId,
  fileName: z.string(),
});

export type GroupMention = z.infer<typeof GroupMention>;
export type UserMention = z.infer<typeof UserMention>;
export type TableMention = z.infer<typeof TableMention>;
export type DataframeMention = z.infer<typeof DataframeMention>;
export type HexMention = z.infer<typeof HexMention>;
export type TaoProjectMention = z.infer<typeof TaoProjectMention>;
export type HexMentionInput = z.infer<typeof HexMentionInput>;
export type EmojiInputElement = z.infer<typeof EmojiInputElement>;
export type CellMention = z.infer<typeof CellMention>;
export type SemanticDatasetMention = z.infer<typeof SemanticDatasetMention>;
export type SemanticViewMention = z.infer<typeof SemanticViewMention>;
export type FileMention = z.infer<typeof FileMention>;

export const MentionElement = z.union([
  UserMention,
  GroupMention,
  TableMention,
  DataframeMention,
  HexMention,
  TaoProjectMention,
  CellMention,
  SemanticDatasetMention,
  SemanticViewMention,
  FileMention,
]);
export type MentionElement = z.infer<typeof MentionElement>;

export const LinkElement = z.object({
  type: RichTextLink,
  url: z.string(),
  target: z.string().optional(),
  children: z.array(FormattedText),
});
export type LinkElement = z.infer<typeof LinkElement>;

export const ReferenceElement = z.object({
  type: RichTextReference,
  id: z.string(),
  value: z.string(),
  children: z.array(FormattedText),
});
export type ReferenceElement = z.infer<typeof ReferenceElement>;

export const ImageAlignment = z.enum(["left", "center", "right"]);
export type ImageAlignment = z.infer<typeof ImageAlignment>;

export const ImageElement = z.object({
  type: RichTextImage,
  src: z.string(),
  width: z.number().optional(),
  alignment: ImageAlignment.optional(),
  children: z.array(FormattedText),
});
export type ImageElement = z.infer<typeof ImageElement>;

export const InlineElement = z.union([
  FormattedText,
  MentionElement,
  HexMentionInput,
  EmojiInputElement,
  LinkElement,
  ReferenceElement,
  ImageElement,
]);
export type InlineElement = z.infer<typeof InlineElement>;

export const TextElement = z.object({
  type: RichTextTextType,
  children: z.array(InlineElement),
  indent: z.number().optional(),
});
export type TextElement = z.infer<typeof TextElement>;

export const ListItemContentElement = z.object({
  type: RichTextLIC,
  children: z.array(InlineElement),
});
export type ListItemContentElement = z.infer<typeof ListItemContentElement>;

export const ListItemElement: z.ZodType<
  {
    type: RichTextLI;
    children: (ListItemContentElement | ListElement)[];
  },
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z.object({
    type: RichTextLI,
    children: z.array(z.union([ListItemContentElement, ListElement])),
  }),
);
export type ListItemElement = z.infer<typeof ListItemElement>;

export const ListElement = z.object({
  type: RichTextListType,
  children: z.array(ListItemElement),
});
export type ListElement = z.infer<typeof ListElement>;

export const RichTextHR = z.literal("hr");
export type RichTextHR = z.infer<typeof RichTextHR>;

export const HRElement = z.object({
  type: RichTextHR,
  children: z.array(FormattedText),
});
export type HRElement = z.infer<typeof HRElement>;

/**
 * Every alternative must appear exactly once, including transitively through
 * nested unions like `MentionElement`. runtypes' cycle detection records each
 * (value, runtype) pair the first time it is checked and returns success the
 * second time, so a duplicated alternative makes the union accept any object.
 */
export const CustomElement = z.union([
  TextElement,
  MentionElement,
  ListElement,
  ListItemElement,
  ListItemContentElement,
  LinkElement,
  ReferenceElement,
  HRElement,
  ImageElement,
  HexMentionInput,
  EmojiInputElement,
]);
export type CustomElement = z.infer<typeof CustomElement>;

export const Descendant = z.union([CustomElement, FormattedText]);
export type Descendant = z.infer<typeof Descendant>;

export const RichTextDocument = z.array(CustomElement);
export type RichTextDocument = z.infer<typeof RichTextDocument>;

export type RichTextComputedReferences = {
  [referenceElementId: string]: string;
};
